import { useEffect, useMemo, useState } from "react";
import { deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "./useAuth";
import { useCouple } from "./useCouple";
import type { CoupleLocation } from "../types";

const REFRESH_MS = 2 * 60 * 1000;

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

// Opt-in, per-device location sharing — mirrors useNotifications.ts's
// enable/disable/busy/error shape (same underlying idea: a browser
// permission that can be granted, denied, or revoked at any time). Enabling
// writes one fresh position immediately and then every REFRESH_MS while this
// stays mounted; disabling deletes the stored doc outright rather than just
// hiding it, so there's nothing left over once you turn it off.
export function useLocationSharing() {
  const { user } = useAuth();
  const { couple, partner } = useCouple();
  const [mine, setMine] = useState<CoupleLocation | null>(null);
  const [partnerLoc, setPartnerLoc] = useState<CoupleLocation | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supported = typeof navigator !== "undefined" && !!navigator.geolocation;
  const enabled = !!mine;

  useEffect(() => {
    if (!couple || !user) {
      setMine(null);
      return;
    }
    return onSnapshot(
      doc(db, "couples", couple.id, "locations", user.uid),
      (snap) => setMine(snap.exists() ? (snap.data() as CoupleLocation) : null),
      () => setError("Standort konnte nicht geladen werden.")
    );
  }, [couple, user]);

  useEffect(() => {
    if (!couple || !partner) {
      setPartnerLoc(null);
      return;
    }
    // Swallowed rather than surfaced as `error` — a failure here means the
    // partner's doc couldn't be read (e.g. they never enabled sharing), not
    // a problem with *my* side, so it shouldn't show as my own error state.
    return onSnapshot(
      doc(db, "couples", couple.id, "locations", partner.uid),
      (snap) => setPartnerLoc(snap.exists() ? (snap.data() as CoupleLocation) : null),
      () => setPartnerLoc(null)
    );
  }, [couple, partner]);

  async function writeCurrentPosition(): Promise<void> {
    if (!couple || !user) return;
    await new Promise<void>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setDoc(doc(db, "couples", couple.id, "locations", user.uid), {
            uid: user.uid,
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            updatedAt: serverTimestamp(),
          })
            .then(() => resolve())
            .catch(reject);
        },
        reject,
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
      );
    });
  }

  async function enable() {
    if (!couple || !user || !supported) return;
    setBusy(true);
    setError(null);
    try {
      await writeCurrentPosition();
    } catch {
      setError("Standort konnte nicht abgerufen werden — bitte Standortzugriff für diese Seite erlauben.");
    } finally {
      setBusy(false);
    }
  }

  // Same thing as enable(), just under a name that reads right for a
  // "refresh now" tap once sharing is already on (see Home.tsx) — and with
  // its own error copy, since "aktiviere Standortzugriff" would be
  // confusing to see on a tap that's just asking for a fresh fix.
  async function refresh() {
    if (!couple || !user || !supported) return;
    setBusy(true);
    setError(null);
    try {
      await writeCurrentPosition();
    } catch {
      setError("Standort konnte nicht aktualisiert werden.");
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!couple || !user) return;
    setBusy(true);
    try {
      await deleteDoc(doc(db, "couples", couple.id, "locations", user.uid));
    } finally {
      setBusy(false);
    }
  }

  // Refreshes immediately whenever sharing turns on (covers "just reopened
  // the app after it sat closed for hours" — the old code only set up the
  // interval, which waits a full REFRESH_MS before its first tick) and then
  // every REFRESH_MS while this stays mounted and the tab is open.
  useEffect(() => {
    if (!enabled || !couple || !user) return;
    writeCurrentPosition().catch(() => {});
    const id = setInterval(() => {
      writeCurrentPosition().catch(() => {});
    }, REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, couple, user]);

  // Covers "app was backgrounded/suspended for a while, then brought back to
  // the foreground" — a setInterval doesn't reliably fire while a tab/PWA is
  // backgrounded, so without this the distance could sit stale for hours
  // until the next time the component happens to remount.
  useEffect(() => {
    if (!enabled || !couple || !user) return;
    function onVisible() {
      if (document.visibilityState === "visible") writeCurrentPosition().catch(() => {});
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, couple, user]);

  const distanceKm = useMemo(() => {
    if (!mine || !partnerLoc) return null;
    return haversineKm(mine.lat, mine.lng, partnerLoc.lat, partnerLoc.lng);
  }, [mine, partnerLoc]);

  return { supported, enabled, busy, error, enable, disable, refresh, distanceKm, partnerEnabled: !!partnerLoc };
}
